
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Save, RotateCcw, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export function SystemInstructionsEditor() {
  const [instructions, setInstructions] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadInstructions();
  }, []);

  useEffect(() => {
    setHasChanges(instructions !== originalInstructions);
  }, [instructions, originalInstructions]);

  const loadInstructions = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'system_instructions')
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const instructionsText = data?.setting_value || '';
      setInstructions(instructionsText);
      setOriginalInstructions(instructionsText);
    } catch (error: any) {
      console.error('Error loading instructions:', error);
      setError('Kunne ikke laste systeminstruksjoner');
      toast({
        title: "Feil ved lasting",
        description: "Kunne ikke laste systeminstruksjoner",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveInstructions = async () => {
    try {
      setSaving(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('system_settings')
        .update({
          setting_value: instructions,
          updated_by: (await supabase.auth.getUser()).data.user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'system_instructions');

      if (updateError) {
        throw updateError;
      }

      // Logg endringen i audit_log
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('audit_log')
          .insert({
            user_id: user.id,
            action: 'UPDATE',
            table_name: 'system_settings',
            record_id: 'system_instructions',
            old_values: { setting_value: originalInstructions },
            new_values: { setting_value: instructions }
          });
      }

      setOriginalInstructions(instructions);
      toast({
        title: "Lagret",
        description: "Systeminstruksjonene er oppdatert",
      });
    } catch (error: any) {
      console.error('Error saving instructions:', error);
      setError('Kunne ikke lagre endringene');
      toast({
        title: "Feil ved lagring",
        description: "Kunne ikke lagre systeminstruksjonene",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setInstructions(originalInstructions);
    setError(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Laster systeminstruksjoner...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Systeminstruksjoner
              {hasChanges && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  Ulagrede endringer
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Rediger Klaras systeminstruksjoner som styrer hvordan AI-assistenten oppfører seg
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={resetChanges}
              disabled={!hasChanges || saving}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Tilbakestill
            </Button>
            <Button
              onClick={saveInstructions}
              disabled={!hasChanges || saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {saving ? 'Lagrer...' : 'Lagre endringer'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <label htmlFor="instructions" className="text-sm font-medium">
            Instruksjonstekst (Markdown-format)
          </label>
          <Textarea
            id="instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Skriv inn systeminstruksjoner for Klara..."
            className="min-h-[400px] font-mono text-sm"
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground">
            {instructions.length} tegn • Støtter Markdown-formatering
          </p>
        </div>

        {hasChanges && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Du har ulagrede endringer. Husk å lagre før du forlater siden.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
